import React from "react";
import {
  FaUsers,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaChartLine,
  FaBullseye,
  FaCheck,
  FaArrowRight,
  FaStar,
  FaHandshake,
  FaSearch,
  FaHashtag,
  FaDollarSign,
  FaCog,
  FaSync,
  FaRocket,
  FaPalette,
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import influencerBg from "../../assets/Influencer-Marketing.jpg";

const InfluencerMarketing = () => {
  const services = [
    {
      icon: FaSearch,
      title: "Influencer Discovery & Vetting",
      description: "Identify and vet the perfect influencers for your brand niche with detailed analytics",
      features: ["Audience Analysis", "Engagement Validation", "Brand Fit Assessment", "Performance Tracking", "Fake Follower Detection", "Content Quality Review"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaHandshake,
      title: "Campaign Strategy & Management",
      description: "End-to-end management of influencer partnerships and campaign execution",
      features: ["Campaign Planning", "Contract Negotiation", "Content Briefing", "Timeline Management", "Performance Monitoring", "Relationship Building"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaInstagram,
      title: "Content Strategy & Creation",
      description: "Creative direction and content planning for maximum engagement and impact",
      features: ["Content Calendar", "Creative Direction", "Hashtag Strategy", "Story Concepts", "Reel Production", "Post Optimization"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaChartLine,
      title: "Performance Analytics & ROI",
      description: "Comprehensive tracking and measurement of campaign effectiveness and return on investment",
      features: ["Engagement Tracking", "Conversion Monitoring", "ROI Calculation", "Audience Growth", "Brand Sentiment", "Competitive Analysis"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaHashtag,
      title: "Hashtag Campaigns & UGC",
      description: "Branded hashtag strategies and user-generated content collection",
      features: ["Hashtag Creation", "UGC Collection", "Community Building", "Contest Management", "Content Rights", "Amplification Strategy"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaUsers,
      title: "Community & Relationship Building",
      description: "Build authentic relationships with micro and nano influencers for long-term partnerships",
      features: ["Talent Database", "Relationship Management", "Nurture Campaigns", "Exclusive Programs", "Feedback Collection", "Loyalty Development"],
      color: "from-orange-500 to-amber-500"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Strategy & Planning",
      description: "Develop influencer marketing strategy aligned with business objectives",
      icon: FaCog
    },
    {
      step: "02",
      title: "Influencer Discovery",
      description: "Identify and vet influencers that match your brand values and target audience",
      icon: FaSearch
    },
    {
      step: "03",
      title: "Outreach & Negotiation",
      description: "Connect with influencers and negotiate partnership terms and deliverables",
      icon: FaHandshake
    },
    {
      step: "04",
      title: "Content Development",
      description: "Create campaign briefs and collaborate on content creation",
      icon: FaPalette
    },
    {
      step: "05",
      title: "Campaign Execution",
      description: "Launch and manage influencer campaigns across platforms",
      icon: FaRocket
    },
    {
      step: "06",
      title: "Measurement & Optimization",
      description: "Analyze performance and optimize future campaigns",
      icon: FaChartLine
    }
  ];

  const campaignTypes = [
    {
      category: "Sponsored Content",
      items: ["Instagram Posts", "TikTok Videos", "YouTube Reviews", "Instagram Stories", "Reels & Shorts", "Live Streams"],
      icon: FaInstagram
    },
    {
      category: "Affiliate Programs",
      items: ["Discount Codes", "Tracked Links", "Commission-Based", "Product Gifting", "Exclusive Offers", "Limited Promotions"],
      icon: FaDollarSign
    },
    {
      category: "Brand Partnerships",
      items: ["Brand Ambassadors", "Product Collaborations", "Event Appearances", "Takeovers", "Content Series", "Long-term Contracts"],
      icon: FaStar
    }
  ];

  const features = [
    {
      title: "Audience Targeting",
      description: "Precise targeting based on demographics, interests, and engagement patterns",
      icon: FaBullseye
    },
    {
      title: "Performance Analytics",
      description: "Detailed tracking of engagement, reach, conversions, and ROI metrics",
      icon: FaChartLine
    },
    {
      title: "Relationship Management",
      description: "Centralized platform for managing all influencer relationships and communications",
      icon: FaUsers
    },
    {
      title: "Content Amplification",
      description: "Strategies to maximize reach and engagement of influencer content",
      icon: FaSync
    }
  ];

  const platforms = [
    {
      name: "Instagram",
      description: "Visual platform perfect for lifestyle, fashion, and product-focused campaigns",
      strength: "Visual storytelling"
    },
    {
      name: "TikTok",
      description: "Short-form video platform ideal for viral content and younger demographics",
      strength: "Viral potential"
    },
    {
      name: "YouTube",
      description: "Long-form video platform for in-depth reviews and tutorials",
      strength: "Detailed content"
    },
    {
      name: "LinkedIn",
      description: "Professional network for B2B and thought leadership campaigns",
      strength: "Professional audience"
    }
  ];

  const influencerTiers = [
    {
      name: "Mega Influencers",
      description: "1M+ followers with massive reach and brand awareness potential",
      icon: FaStar,
      metrics: ["1M+ Followers", "Massive Reach", "High Awareness", "Premium Rates"]
    },
    {
      name: "Macro Influencers",
      description: "100K-1M followers offering balance of reach and engagement",
      icon: FaUsers,
      metrics: ["100K-1M Followers", "Good Engagement", "Niche Authority", "Mid-range Rates"]
    },
    {
      name: "Micro Influencers",
      description: "10K-100K followers with high engagement and niche expertise",
      icon: FaBullseye,
      metrics: ["10K-100K Followers", "High Engagement", "Authenticity", "Cost-effective"]
    },
    {
      name: "Nano Influencers",
      description: "1K-10K followers offering hyper-local and community-focused reach",
      icon: FaHandshake,
      metrics: ["1K-10K Followers", "Local Influence", "Community Trust", "Affordable Rates"]
    }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section */}
        <div
          className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${influencerBg})` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-orange-500 rounded-full filter blur-3xl opacity-20"></div>
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-amber-500 rounded-full filter blur-3xl opacity-20"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-white to-white bg-clip-text text-transparent pb-3">
                Influencer Marketing
              </h1>
              <p className="text-xl text-gray-200 mb-10 leading-relaxed max-w-3xl mx-auto">
                Connect with authentic voices to amplify your brand. Strategic influencer partnerships that drive engagement, trust, and conversions.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Influencer Marketing Services
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                End-to-end influencer solutions to build authentic brand connections and drive measurable results
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-orange-500/50 hover:transform hover:-translate-y-2"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`bg-gradient-to-r ${service.color} p-4 rounded-2xl text-white text-2xl shadow-lg`}>
                      <service.icon />
                    </div>
                    <div className="text-2xl font-bold text-white">{service.title}</div>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-lg mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center text-gray-300 group/item hover:text-orange-400 transition-colors duration-200"
                      >
                        <div className="bg-orange-500/20 p-1 rounded-lg mr-3 group-hover/item:bg-orange-500/30 transition-colors duration-200">
                          <FaCheck className="text-orange-400 text-sm" />
                        </div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Campaign Types Section */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 opacity-30"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Campaign Types
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Diverse influencer marketing strategies for different business objectives
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {campaignTypes.map((type, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group hover:border-orange-200"
                >
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-2xl p-4 rounded-2xl inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <type.icon />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {type.category}
                  </h3>
                  <div className="space-y-3">
                    {type.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center text-gray-700 group/item hover:text-orange-600 transition-colors duration-200"
                      >
                        <div className="bg-green-100 p-1 rounded-lg mr-3 group-hover/item:bg-green-200 transition-colors duration-200">
                          <FaCheck className="text-green-600 text-sm" />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Advanced Influencer Marketing Features
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Leverage cutting-edge tools and methodologies for maximum campaign impact
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-orange-500/50"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xl p-3 rounded-xl shadow-lg">
                      <feature.icon />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Platform Expertise */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Platform Expertise
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're experts in all major social media platforms
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {platforms.map((platform, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group hover:border-orange-200 text-center"
                >
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xl p-4 rounded-2xl inline-flex mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    {platform.name === "Instagram" && <FaInstagram />}
                    {platform.name === "TikTok" && <FaTiktok />}
                    {platform.name === "YouTube" && <FaYoutube />}
                    {platform.name === "LinkedIn" && <FaUsers />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{platform.description}</p>
                  <div className="text-orange-600 text-sm font-semibold">
                    {platform.strength}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Influencer Tiers Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full translate-y-48 -translate-x-48"></div>
          
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Influencer Tiers & Selection
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Strategic selection of influencers based on campaign goals and budget
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {influencerTiers.map((tier, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 text-white rounded-2xl p-8 h-full transform group-hover:scale-105 transition-all duration-300">
                    <div className="flex items-center justify-center mb-4">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xl p-4 rounded-2xl">
                        <tier.icon />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-center text-white">{tier.name}</h3>
                    <p className="text-gray-300 text-sm mb-4 text-center">{tier.description}</p>
                    <div className="space-y-2">
                      {tier.metrics.map((metric, idx) => (
                        <div key={idx} className="flex items-center text-orange-300 text-sm">
                          <FaCheck className="text-orange-400 mr-2 text-xs" />
                          {metric}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full translate-y-48 -translate-x-48"></div>
          
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Our Influencer Marketing Process
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                A systematic approach to building successful influencer partnerships
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {process.map((step, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 text-white rounded-2xl p-8 h-full transform group-hover:scale-105 transition-all duration-500">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="text-3xl font-bold text-orange-400 bg-orange-500/10 px-4 py-2 rounded-xl border border-orange-500/20">
                        {step.step}
                      </div>
                      <div className="text-orange-400">
                        <step.icon className="text-2xl" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-white">{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative pt-10 pb-10 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-40"></div>
          
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-orange-500 rounded-full filter blur-3xl opacity-10"></div>
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-amber-500 rounded-full filter blur-3xl opacity-10"></div>
          </div>
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Amplify Your Brand?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let us connect you with authentic influencers who resonate with your audience. 
              From discovery to campaign execution, we'll build partnerships that drive real results.
            </p>
            <div className="flex justify-center items-center mt-6">
              <button 
                onClick={() => (window.location.href = "/contact-us")}
                className="group bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-12 py-5 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/25 flex items-center gap-3"
              >
                Launch Influencer Campaign
                <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default InfluencerMarketing;